const SENSITIVE_KEYS = [
	'token',
	'access_token',
	'accessToken',
	'userToken',
	'refreshToken',
	'refresh_token',
	'tokenTimestamp',
	'token_timestamp',
	'userFields',
	'user_fields',
	'user',
	'userId',
	'user_id',
	'displayName',
	'currency',
	'app',
	'lang',
	'locale',
	'handoff',
	'origin',
	'logoutRedirectionUrl',
	'environment',
]

interface AppSessionTokens {
	effectiveToken: string
	effectiveRefreshToken: string | null
}

/**
 * Checks for session handoff parameters in the URL hash or query string on app boot or route entry.
 * If present, ingests the session into sessionStorage, updates language settings, and scrubs
 * the sensitive parameters from the address bar.
 */
export function acceptHandoff(): void {
	if (typeof window === 'undefined' || typeof document === 'undefined') return

	try {
		const url = new URL(window.location.href)
		const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
		const searchParams = url.searchParams

		const getParam = (key: string): string | null => hashParams.get(key) ?? searchParams.get(key)

		const token = getParam('token') ?? getParam('access_token') ?? getParam('accessToken') ?? getParam('userToken')

		if (token) {
			handleDirectHandoff(token, getParam, url, searchParams, hashParams)
			return
		}

		const handoffToken = getParam('handoff')
		if (handoffToken) {
			handleStorageHandoff(handoffToken, url, searchParams, hashParams)
		}
	} catch {
		// Silent catch if URL is invalid or parsing fails
	}
}

function handleDirectHandoff(
	token: string,
	getParam: (key: string) => string | null,
	url: URL,
	searchParams: URLSearchParams,
	hashParams: URLSearchParams,
): void {
	const refreshToken = getParam('refreshToken') ?? getParam('refresh_token')
	const appStr = getParam('app')
	const userFieldsStr = getParam('userFields') ?? getParam('user_fields') ?? getParam('user')
	const lang = getParam('lang') ?? getParam('locale')

	sessionStorage.setItem('token', token)
	sessionStorage.setItem('token_timestamp', Date.now().toString())

	if (refreshToken) {
		sessionStorage.setItem('refresh_token', refreshToken)
	}

	saveAppSession(token, refreshToken, appStr, userFieldsStr, getParam)

	if (lang) {
		sessionStorage.setItem('locale', lang)
		localStorage.setItem('language', lang)
	}

	cleanHandoffUrl(url, searchParams, hashParams, SENSITIVE_KEYS)
}

function extractTokensFromApp(
	appStr: string | null,
	defaultToken: string,
	defaultRefreshToken: string | null,
): { tokens: AppSessionTokens; appUser: Record<string, unknown> | null } {
	let effectiveToken = defaultToken
	let effectiveRefreshToken = defaultRefreshToken
	let appUser: Record<string, unknown> | null = null

	if (!appStr) {
		return { tokens: { effectiveToken, effectiveRefreshToken }, appUser }
	}

	const parsedApp = parseJsonSafely(appStr) as Record<string, unknown> | null
	if (parsedApp && typeof parsedApp === 'object') {
		effectiveToken = (parsedApp['access_token'] as string) ?? (parsedApp['token'] as string) ?? defaultToken
		effectiveRefreshToken = (parsedApp['refresh_token'] as string) ?? (parsedApp['refreshToken'] as string) ?? defaultRefreshToken

		const rawAppUser = parsedApp['user_fields'] ?? parsedApp['user'] ?? parsedApp['userFields']
		if (rawAppUser && typeof rawAppUser === 'object') {
			appUser = rawAppUser as Record<string, unknown>
		} else if (typeof rawAppUser === 'string') {
			appUser = (parseJsonSafely(rawAppUser) as Record<string, unknown>) ?? null
		}
	}

	return { tokens: { effectiveToken, effectiveRefreshToken }, appUser }
}

function extractUserFields(
	appUser: Record<string, unknown> | null,
	userFieldsStr: string | null,
	getParam: (key: string) => string | null,
): Record<string, unknown> {
	let userObj = appUser ?? {}

	if (Object.keys(userObj).length === 0 && userFieldsStr) {
		const rawUser = parseJsonSafely(userFieldsStr)
		if (rawUser && typeof rawUser === 'object') {
			userObj = rawUser as Record<string, unknown>
		}
	}

	const id = getParam('userId') ?? getParam('user_id')
	const name = getParam('displayName') ?? getParam('userName') ?? getParam('username') ?? getParam('name')
	const currency = getParam('currency') ?? getParam('CURRENCY')
	const language = getParam('lang') ?? getParam('locale')

	if (!userObj['ID'] && id) userObj['ID'] = id
	if (!userObj['NAME'] && name) userObj['NAME'] = name
	if (!userObj['CURRENCY'] && currency) userObj['CURRENCY'] = currency
	if (!userObj['LANGUAGE'] && language) userObj['LANGUAGE'] = language

	parseNestedJsonProperties(userObj)

	return userObj
}

function parseNestedJsonProperties(userObj: Record<string, unknown>): void {
	const nestedKeys = ['USER_BETSLIP', 'WHITE_LABEL_BET_SLIP', 'BET_CONFIG']
	for (const key of nestedKeys) {
		if (typeof userObj[key] === 'string') {
			userObj[key] = parseJsonSafely(userObj[key])
		}
	}
}

function saveAppSession(
	token: string,
	refreshToken: string | null,
	appStr: string | null,
	userFieldsStr: string | null,
	getParam: (key: string) => string | null,
): void {
	const { tokens, appUser } = extractTokensFromApp(appStr, token, refreshToken)
	const userObj = extractUserFields(appUser, userFieldsStr, getParam)

	const appPayload = {
		access_token: tokens.effectiveToken,
		token: tokens.effectiveToken,
		...(tokens.effectiveRefreshToken ? { refresh_token: tokens.effectiveRefreshToken } : {}),
		user_fields: userObj,
		user: userObj,
	}

	sessionStorage.setItem('app', JSON.stringify(appPayload))
	if (tokens.effectiveToken) {
		sessionStorage.setItem('token', tokens.effectiveToken)
	}
	if (tokens.effectiveRefreshToken) {
		sessionStorage.setItem('refresh_token', tokens.effectiveRefreshToken)
	}
}

function handleStorageHandoff(handoffToken: string, url: URL, searchParams: URLSearchParams, hashParams: URLSearchParams): void {
	const raw = localStorage.getItem(`handoff:${handoffToken}`)
	if (raw) {
		applyStoragePayload(raw)
		localStorage.removeItem(`handoff:${handoffToken}`)
	}

	cleanHandoffUrl(url, searchParams, hashParams, ['handoff'])
}

function applyStoragePayload(raw: string): void {
	const data = parseJsonSafely(raw) as { ts?: number; payload?: Record<string, string> } | null
	if (data?.ts && Date.now() - data.ts <= 15000 && data.payload) {
		Object.entries(data.payload).forEach(([k, v]) => {
			if (k === 'app') {
				// Normalize app payload if it contains 'user' instead of 'user_fields'
				const parsed = parseJsonSafely(v) as Record<string, unknown> | null
				if (parsed && typeof parsed === 'object') {
					if (!parsed['user_fields'] && parsed['user']) {
						parsed['user_fields'] = parsed['user']
					}
					if (!parsed['access_token'] && parsed['token']) {
						parsed['access_token'] = parsed['token']
					}
					sessionStorage.setItem('app', JSON.stringify(parsed))
					return
				}
			}
			sessionStorage.setItem(k, v)
		})
	}
}

function cleanHandoffUrl(url: URL, searchParams: URLSearchParams, hashParams: URLSearchParams, keysToRemove: string[]): void {
	keysToRemove.forEach((k) => {
		searchParams.delete(k)
		hashParams.delete(k)
	})

	const cleanSearch = searchParams.toString() ? `?${searchParams.toString()}` : ''
	const cleanHash = hashParams.toString() ? `#${hashParams.toString()}` : ''
	window.history.replaceState({}, document.title, url.pathname + cleanSearch + cleanHash)
}

function parseJsonSafely(str: string | null): unknown {
	if (!str) return null
	try {
		return JSON.parse(str)
	} catch {
		return null
	}
}
